import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiPlus, FiEdit2, FiTrash2, FiSearch, FiRefreshCw,
    FiShield, FiUser, FiUsers, FiBriefcase,
    FiBook, FiDollarSign, FiHome, FiFileText,
    FiLock, FiUnlock, FiKey, FiArchive,
} from "react-icons/fi";
import userService from "../../services/userService";
import ConfirmModal from "../../components/common/ConfirmModal";
import { useApp } from "../../context/AppContext";

// ─── Constants ────────────────────────────────────────────────────────────────
const ROLES = [
    "admin",
    "teacher",
    "librarian",
    "accountant",
    "warden",
    "clerk",
    "student",
    "parent",
    "storekeeper",
];

const ROLE_CONFIG = {
    admin: { color: "#dc2626", bg: "#fee2e2", icon: FiShield, label: "Admin" },
    teacher: { color: "#7c3aed", bg: "#ede9fe", icon: FiBriefcase, label: "Teacher" },
    librarian: { color: "#0284c7", bg: "#e0f2fe", icon: FiBook, label: "Librarian" },
    accountant: { color: "#059669", bg: "#d1fae5", icon: FiDollarSign, label: "Accountant" },
    warden: { color: "#d97706", bg: "#fef3c7", icon: FiHome, label: "Warden" },
    clerk: { color: "#4b5563", bg: "#f3f4f6", icon: FiFileText, label: "Clerk" },
    student: { color: "#003b67", bg: "#e8f1f8", icon: FiUser, label: "Student" },
    parent: { color: "#0d9488", bg: "#ccfbf1", icon: FiUsers, label: "Parent" },
    storekeeper: { color: "#0d9488", bg: "#ccfbf1", icon: FiArchive, label: "Storekeeper" },
};

// ─── Reset Password Modal ─────────────────────────────────────────────────────
function ResetPasswordModal({ user, onClose, onDone }) {
    const [newPw, setNewPw] = useState("");
    const [loading, setLoading] = useState(false);

    const handleReset = async () => {
        if (!newPw || newPw.length < 6) {
            toast.error("Password must be at least 6 characters.");
            return;
        }
        setLoading(true);
        try {
            const res = await userService.resetPassword(user._id, newPw);
            toast.success(res.message);
            onDone();
            onClose();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal" style={{ width: 380 }} onClick={(e) => e.stopPropagation()}>
                <h3 style={{ marginBottom: 4 }}>Reset Password</h3>
                <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1rem" }}>
                    Resetting password for <strong>{user.displayName || user.username}</strong>.
                    They will be required to change it on next login.
                </p>
                <div className="form-group">
                    <label className="form-label">New Password *</label>
                    <input
                        className="form-control"
                        type="text"
                        value={newPw}
                        onChange={(e) => setNewPw(e.target.value)}
                        placeholder="Min. 6 characters"
                        autoFocus
                    />
                    <span className="form-hint">
                        Tip: for students use DOB (e.g. 15082005) or enrollment no.
                    </span>
                </div>
                <div className="modal-actions" style={{ marginTop: "1.25rem" }}>
                    <button className="btn btn-outline" onClick={onClose}>Cancel</button>
                    <button
                        className="btn btn-primary"
                        onClick={handleReset}
                        disabled={loading || newPw.length < 6}
                    >
                        <FiKey size={13} /> {loading ? "Resetting…" : "Reset Password"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function UserList() {
    const navigate = useNavigate();
    const { user: currentUser } = useApp();
    const isSuperAdmin = currentUser?.role === "superadmin";

    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);

    // Filters
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [activeFilter, setActiveFilter] = useState("");
    const [page, setPage] = useState(1);

    // Modal state
    const [resetTarget, setResetTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [toggling, setToggling] = useState(null);

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await userService.getAll({
                search,
                role: roleFilter,
                isActive: activeFilter,
                page,
                limit: 25,
            });
            setUsers(Array.isArray(res.data) ? res.data : []);
            setPagination(res.pagination);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    }, [search, roleFilter, activeFilter, page]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);
    useEffect(() => { setPage(1); }, [search, roleFilter, activeFilter]);

    // ── Deactivate / Activate ─────────────────────────────────────────────────
    const handleToggleActive = async (user) => {
        setToggling(user._id);
        try {
            const res = await userService.update(user._id, { isActive: !user.isActive });
            toast.success(`${res.data.displayName || res.data.username} ${res.data.isActive ? "activated" : "deactivated"}.`);
            fetchUsers();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setToggling(null);
        }
    };

    // ── Delete ────────────────────────────────────────────────────────────────
    const handleDelete = async () => {
        setDeleting(true);
        try {
            const res = await userService.remove(deleteTarget._id);
            toast.success(res.message);
            setDeleteTarget(null);
            fetchUsers();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setDeleting(false);
        }
    };

    const clearFilters = () => {
        setSearch(""); setRoleFilter(""); setActiveFilter("");
    };
    const hasFilters = search || roleFilter || activeFilter !== "";

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
                <div>
                    <h1 className="page-title">User Accounts</h1>
                    <p className="page-subtitle">
                        {pagination?.total ?? 0} account{pagination?.total !== 1 ? "s" : ""} total
                    </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/users/bulk-students")}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                    >
                        <FiUsers size={13} /> Bulk Student Accounts
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/users/new")}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                    >
                        <FiPlus size={14} /> Add Account
                    </button>
                </div>
            </div>

            {/* ── Role summary chips ── */}
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem", flexWrap: "wrap" }}>
                {ROLES.map((r) => {
                    const rc = ROLE_CONFIG[r];
                    const Icon = rc.icon;
                    const count = users.filter((u) => u.role === r).length;
                    return (
                        <div
                            key={r}
                            onClick={() => setRoleFilter(roleFilter === r ? "" : r)}
                            style={{
                                display: "flex", alignItems: "center", gap: "0.4rem",
                                padding: "5px 12px", borderRadius: 99, fontSize: 12.5,
                                fontWeight: 600, cursor: "pointer",
                                background: roleFilter === r ? rc.bg : "#f1f5f9",
                                color: roleFilter === r ? rc.color : "#64748b",
                                border: `1px solid ${roleFilter === r ? rc.color + "50" : "#e2e8f0"}`,
                                transition: "all 0.15s",
                            }}
                        >
                            <Icon size={12} /> {rc.label}
                            <span style={{ background: roleFilter === r ? rc.color : "#e2e8f0", color: roleFilter === r ? "#fff" : "#64748b", padding: "1px 6px", borderRadius: 99, fontSize: 11, fontWeight: 700 }}>
                                {pagination?.total !== undefined && roleFilter === r ? pagination.total : count}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className="card">
                {/* ── Filters ── */}
                <div style={{ padding: "0.75rem 1rem", borderBottom: "1px solid var(--border)", display: "flex", gap: "0.65rem", flexWrap: "wrap", alignItems: "center" }}>
                    <div className="search-bar" style={{ flex: 1, minWidth: 200 }}>
                        <FiSearch size={14} color="#9ca3af" />
                        <input
                            placeholder="Search username or name…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="form-control"
                        style={{ width: 155, fontSize: 13 }}
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                    >
                        <option value="">All Roles</option>
                        {ROLES.map((r) => <option key={r} value={r}>{ROLE_CONFIG[r].label}</option>)}
                    </select>
                    <select
                        className="form-control"
                        style={{ width: 140, fontSize: 13 }}
                        value={activeFilter}
                        onChange={(e) => setActiveFilter(e.target.value)}
                    >
                        <option value="">All Status</option>
                        <option value="true">Active only</option>
                        <option value="false">Inactive only</option>
                    </select>
                    <button
                        className="btn btn-outline btn-sm"
                        onClick={fetchUsers}
                        style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}
                    >
                        <FiRefreshCw size={12} />
                    </button>
                    {hasFilters && (
                        <button className="btn btn-outline btn-sm" onClick={clearFilters}>Clear</button>
                    )}
                </div>

                {/* ── Table ── */}
                {loading ? (
                    <div className="loading-center"><div className="spinner" />Loading accounts…</div>
                ) : users.length === 0 ? (
                    <div className="empty-state">
                        <FiUser size={48} style={{ opacity: 0.2, display: "block", margin: "0 auto 0.75rem" }} />
                        <p>{hasFilters ? "No accounts match your filters." : "No user accounts yet."}</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table>
                            <thead>
                                <tr>
                                    <th>#</th>
                                    {isSuperAdmin && <th>College</th>}
                                    <th>Username</th>
                                    <th>Name</th>
                                    <th style={{ textAlign: "center" }}>Role</th>
                                    <th>Linked To</th>
                                    <th style={{ textAlign: "center" }}>Status</th>
                                    <th style={{ textAlign: "center" }}>Pw Change</th>
                                    <th style={{ textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user, i) => {
                                    const rc = ROLE_CONFIG[user.role] || ROLE_CONFIG.admin;
                                    const Icon = rc.icon;
                                    const linked =
                                        user.linkedEmployee
                                            ? `${user.linkedEmployee.fullName} (${user.linkedEmployee.employeeCode})`
                                            : user.linkedStudent
                                                ? `${user.linkedStudent.fullName} (${user.linkedStudent.enrollmentNo})`
                                                : "—";

                                    return (
                                        <tr key={user._id} style={{ opacity: user.isActive ? 1 : 0.55 }}>
                                            <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                {(page - 1) * 25 + i + 1}
                                            </td>
                                            {isSuperAdmin && (
                                                <td style={{ fontSize: 12 }}>
                                                    {user.college ? (
                                                        <span className="badge badge-info" style={{ fontSize: 10.5, fontWeight: 700 }}>
                                                            {user.college.code || user.college.name}
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: "#94a3b8", fontSize: 11 }}>Central</span>
                                                    )}
                                                </td>
                                            )}
                                            <td>
                                                <code style={{ fontSize: 12.5, background: "#f1f5f9", padding: "2px 6px", borderRadius: 4 }}>
                                                    {user.username}
                                                </code>
                                            </td>
                                            <td style={{ fontWeight: 600, fontSize: 13 }}>
                                                {user.displayName || "—"}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: rc.bg, color: rc.color, padding: "3px 10px", borderRadius: 99, fontSize: 11.5, fontWeight: 700 }}>
                                                    <Icon size={11} /> {rc.label}
                                                </span>
                                            </td>
                                            <td style={{ fontSize: 12.5, color: "var(--text-muted)", maxWidth: 200 }}>
                                                <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {linked}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {user.isActive
                                                    ? <span style={{ fontSize: 11, background: "#dcfce7", color: "#15803d", padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>Active</span>
                                                    : <span style={{ fontSize: 11, background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 99, fontWeight: 600 }}>Inactive</span>
                                                }
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {user.mustChangePassword
                                                    ? <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", padding: "2px 7px", borderRadius: 99, fontWeight: 600 }}>Pending</span>
                                                    : <span style={{ fontSize: 11, color: "var(--text-muted)" }}>—</span>
                                                }
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "0.3rem", justifyContent: "flex-end" }}>
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        title="Edit account"
                                                        onClick={() => navigate(`/users/${user._id}/edit`)}
                                                    >
                                                        <FiEdit2 size={12} />
                                                    </button>
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        title="Reset password"
                                                        onClick={() => setResetTarget(user)}
                                                        style={{ color: "#d97706", borderColor: "#d97706" }}
                                                    >
                                                        <FiKey size={12} />
                                                    </button>
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        title={user.isActive ? "Deactivate account" : "Activate account"}
                                                        onClick={() => handleToggleActive(user)}
                                                        disabled={toggling === user._id}
                                                        style={{ color: user.isActive ? "#6b7280" : "#16a34a", borderColor: user.isActive ? "#6b7280" : "#16a34a" }}
                                                    >
                                                        {user.isActive ? <FiLock size={12} /> : <FiUnlock size={12} />}
                                                    </button>
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        title="Delete account"
                                                        onClick={() => setDeleteTarget(user)}
                                                    >
                                                        <FiTrash2 size={12} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ── Pagination ── */}
                {pagination && pagination.pages > 1 && (
                    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.5rem", padding: "0.75rem" }}>
                        <button className="btn btn-outline btn-sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
                        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Page {page} of {pagination.pages}</span>
                        <button className="btn btn-outline btn-sm" disabled={page === pagination.pages} onClick={() => setPage((p) => p + 1)}>Next →</button>
                    </div>
                )}
            </div>

            {/* ── Modals ── */}
            {resetTarget && (
                <ResetPasswordModal
                    user={resetTarget}
                    onClose={() => setResetTarget(null)}
                    onDone={fetchUsers}
                />
            )}

            {deleteTarget && (
                <ConfirmModal
                    title="Delete User Account"
                    message={`Permanently delete account "${deleteTarget.username}" (${deleteTarget.displayName})? This cannot be undone.`}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleting}
                />
            )}
        </div>
    );
}